export interface Category {
  id: number;
  name: string;
}

export interface Company {
  id: number;
  name: string;
  location?: string;
}

export interface Product {
  id?: number;
  name: string;
  firm: string;
  model: string;
  description: string;
  size: string;
  price: number;
  category: Category;
  stock?: number;
  quantity?: number;
  companies?: Company[];
  headerText?: string;
  imageUrl?: string;
  customBarcode?: string;
  customQrCode?: string;
  // Read-only fields computed by the backend (EAN-13 from ID)
  barcode?: string;
  qrCode?: string;
  // E-commerce cost tracking fields
  acquisitionCost?: number;
  deliveryCost?: number;
  adsCost?: number;
  otherCosts?: number;
  // Soft-delete flag returned by the backend when includeDeleted=true
  isDeleted?: boolean;
}

// Helper function to get barcode value: custom if set, then backend-computed, then fallback to EAN-13 from ID
export function getProductBarcode(product: Product): string {
  if (product.customBarcode && product.customBarcode.trim()) return product.customBarcode.trim();
  if (product.barcode) return product.barcode;
  if (!product.id) return '';
  return formatProductIdAsEAN13(product.id);
}

// Helper function to get QR code value.
// - Custom value if manually set.
// - Otherwise: compact JSON with id + key fields so external QR readers show useful info
//   while the app scan handlers can extract the id to look up the product.
export function getProductQRCode(product: Product): string {
  if (product.customQrCode && product.customQrCode.trim()) return product.customQrCode.trim();
  if (!product.id) return '';
  const payload: Record<string, unknown> = { id: product.id };
  if (product.firm)  payload.firm  = product.firm;
  if (product.model) payload.model = product.model;
  if (product.size)  payload.size  = product.size;
  if (product.price != null) payload.price = product.price;
  if (product.category?.name) payload.category = product.category.name;
  return JSON.stringify(payload);
}

// Helper to build a full-product QR code value. This contains key product data
// (including `id`) serialized as a compact JSON string. Use `encodeURIComponent`
// to make it safe for QR encoding and scanning in different contexts.
export function getProductFullQRCode(product: Product): string {
  if (!product) return '';
  // Build a compact payload with commonly useful fields. Avoid including
  // large binary/image data.
  const payload: any = {
    id: product.id ?? null,
    name: product.name ?? undefined,
    firm: product.firm ?? undefined,
    model: product.model ?? undefined,
    size: product.size ?? undefined,
    price: product.price ?? undefined,
    category: product.category?.name ?? undefined,
    companies: (product.companies || []).map(c => ({ id: c.id, name: c.name })),
    barcode: getProductBarcode(product)
  };
  // Remove undefined values to keep payload small
  Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);
  return encodeURIComponent(JSON.stringify(payload));
}

// Format Product ID as EAN-13 barcode (12 digits + check digit)
function formatProductIdAsEAN13(productId: number): string {
  // Pad ID to 12 digits
  const paddedId = productId.toString().padStart(12, '0');
  
  // Calculate EAN-13 check digit
  const checkDigit = calculateEAN13CheckDigit(paddedId);
  
  return paddedId + checkDigit;
}

// Calculate EAN-13 check digit
function calculateEAN13CheckDigit(code: string): number {
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    const digit = parseInt(code.charAt(i), 10);
    sum += (i % 2 === 0) ? digit : digit * 3;
  }
  const remainder = sum % 10;
  return (remainder === 0) ? 0 : (10 - remainder);
}

export interface Deposit {
  id: number;
  name: string;
  location?: string;
  description?: string;
  company?: {
    id: number;
    name: string;
  };
}

export interface DepositProduct {
  id?: number;
  deposit?: Deposit; // Optional because backend doesn't always include it
  product: Product;
  quantity: number;
}

export interface InventoryOperation {
  depositId: number;
  productId: number;
  quantity: number;
  operation: 'add' | 'decrease' | 'set';
}
